import { useAppSelector } from 'dux/app/hooks';

import capitalize from 'lodash/fp/capitalize';
import groupBy from 'lodash/fp/groupBy';
import map from 'lodash/map';

import { Box, Text, theme } from '@prose-ui';
import { styled } from '@prose-ui/legacy';

import CheckoutBlock from 'Apps/Checkout/Blocks/CheckoutBlock';

import { useSkincareMinisFirstChargeAbTest } from 'abTesting/skincareMinisFirstCharge/hook';

import type { Currency } from 'constants/currencies';
import { getFrequencyLabelForValue } from 'constants/subscriptions';

import { skincareMinis } from 'assets/content/skincareMinisModal';

import { formatPriceWithCurrency } from 'utils/currencies';
import { getFormatMMDD } from 'utils/dateUtils';
import { convertArrayToStringWithComas } from 'utils/textUtils';

import {
  getCartItemsWithoutCoupons,
  getCartTotalShipping,
  getCurrencyFromCart,
  getHasSkincareMinisInItems,
  getIsFree,
  getSortedDeliveryDates,
  getSubscriptionDiscount,
} from 'dux/checkoutCart/selectors';
import { getHasOrdersByCategory } from 'dux/user/selectors';
import { useCheckoutSubscriptionPreviewQuery } from 'dux/checkout/apiSlice';
import { VARIANT1 } from 'dux/featureFlags/constants/flagVariants';
import { useCreateUserTagMutation } from 'dux/user/apiSlice';

import { useCheckoutCategory } from '../hooks/useCheckoutCategory';

const List = styled.ul`
  padding-left: ${theme.spacing['6x']};
  margin: 0 0 ${theme.spacing['1x']} 0;
`;

export const SubscribeAndSaveSectionDetailed = () => {
  const { variantName: skincareMinisFirstChargeAbTestVariantName, isInExperiment } =
    useSkincareMinisFirstChargeAbTest();
  const hasOrderByCategory = useAppSelector(getHasOrdersByCategory);
  const [postUserTag, userTagPostResult] = useCreateUserTagMutation({
    fixedCacheKey: 'shared-skincare-minis-user-tag',
  });

  if (!isInExperiment || hasOrderByCategory.oneOf || userTagPostResult.isSuccess) {
    return <SubscribeAndSaveSectionDetailedWrapped />;
  }

  if (userTagPostResult.isUninitialized) {
    postUserTag(
      skincareMinisFirstChargeAbTestVariantName === VARIANT1
        ? 'sss_2025_q1_4_week_trial_period'
        : 'sss_2025_q1_5_week_trial_period',
    );
  }
  return null;
};

const SubscribeAndSaveSectionDetailedWrapped = () => {
  const subscriptionDiscount = useAppSelector(getSubscriptionDiscount);
  const sortedDeliveryDates = useAppSelector(getSortedDeliveryDates);
  const cartItems = useAppSelector(getCartItemsWithoutCoupons) as Array<{
    label: string;
    quantity: number;
  }>;
  const hasSkincareMinisInItems = useAppSelector(getHasSkincareMinisInItems);
  const currency = useAppSelector(getCurrencyFromCart) as Currency;
  const checkoutCategory = useCheckoutCategory();
  const isFreeOrder = useAppSelector(getIsFree);
  const cartTotalShipping = useAppSelector(getCartTotalShipping);

  const hasMultipleOfTheSameItemInCart = cartItems.some((cartItem) => cartItem.quantity > 1);

  const {
    data: subscriptionPreviewItems,
    isLoading,
    isError,
  } = useCheckoutSubscriptionPreviewQuery();

  if (isLoading || isError || !subscriptionPreviewItems) {
    return null;
  }

  const subscriptionPreviewItemsByFrequency = groupBy('frequency')(subscriptionPreviewItems);

  const totalPriceSubscriptionItems = subscriptionPreviewItems.reduce(
    (sum, currentSubscriptionPreviewItem) =>
      sum + currentSubscriptionPreviewItem.total_price * currentSubscriptionPreviewItem.quantity,
    0,
  );

  const totalPriceDisplaySubscriptionItems = subscriptionPreviewItems.reduce(
    (sum, currentSubscriptionPreviewItem) =>
      sum +
      currentSubscriptionPreviewItem.total_price_display * currentSubscriptionPreviewItem.quantity,
    0,
  );

  const savingPrice = totalPriceSubscriptionItems - totalPriceDisplaySubscriptionItems;

  return (
    <CheckoutBlock dataTestId="subscribe-and-save" title="Subscribe & save">
      <Box display="flex" flexDirection="column" gap="4x" marginTop="4x">
        <div>
          <Text fontWeight="bold" variant="bodyMd">
            Today’s order —{' '}
            {isFreeOrder || hasSkincareMinisInItems ? 'FREE' : `${subscriptionDiscount}% off`}:
          </Text>
          <List>
            <li>
              <Text color="neutral.900" variant="bodyMd">
                {hasSkincareMinisInItems
                  ? skincareMinis.subscribeAndSaveSection.label
                  : convertArrayToStringWithComas(
                      cartItems.map(({ label, quantity }) =>
                        hasMultipleOfTheSameItemInCart ? `${label} (${quantity})` : label,
                      ),
                    )}
              </Text>
            </li>

            {hasSkincareMinisInItems && Boolean(cartTotalShipping) && (
              <li>
                <Text color="neutral.900" variant="bodyMd">
                  Just pay {formatPriceWithCurrency({ currency, price: cartTotalShipping })}{' '}
                  shipping
                </Text>
              </li>
            )}
            {sortedDeliveryDates.length > 0 && (
              <li>
                <Text color="neutral.900" variant="bodyMd">
                  Arrives between <b>{sortedDeliveryDates[0]}</b> and{' '}
                  <b>{sortedDeliveryDates[sortedDeliveryDates.length - 1]}</b>
                </Text>
              </li>
            )}
          </List>
        </div>
        <div>
          <Text fontWeight="bold" variant="bodyMd">
            Future subscription orders — {subscriptionDiscount}% off:
          </Text>

          {map(subscriptionPreviewItemsByFrequency, (itemsFromFrequency, frequency) => {
            // @ts-expect-error checkoutCart dux not typed yet
            const nextStartDate = getFormatMMDD(itemsFromFrequency[0]?.next_start_date);
            const hasMoreThanOneOfASubscriptionItem = itemsFromFrequency.some(
              // @ts-expect-error checkoutCart dux not typed yet
              (item) => item.quantity > 1,
            );
            return (
              <div key={frequency}>
                <Text fontStyle="italic" variant="bodyMd">
                  Starting <b>{nextStartDate}</b>, charged{' '}
                  <b>
                    {getFrequencyLabelForValue({
                      value: parseInt(frequency, 10),
                      // @ts-expect-error - this function is not in TS yet
                      category: checkoutCategory,
                      noRecommendation: true,
                    }).toLowerCase()}
                  </b>
                </Text>
                <List>
                  {itemsFromFrequency.map(
                    //  @ts-expect-error - groupBy does not infer types correctly
                    ({ product, total_price, total_price_display, quantity }) => (
                      <li key={product}>
                        <Text alignItems="baseline" display="flex" gap="1x" variant="bodyMd">
                          <Text as="s" color="neutral.700" variant="bodyMd">
                            {formatPriceWithCurrency({ currency, price: total_price })}
                          </Text>
                          <Text as="span" color="neutral.900" fontWeight="bold" variant="bodyMd">
                            {formatPriceWithCurrency({ currency, price: total_price_display })}
                          </Text>
                          <Text as="span" variant="bodyMd">
                            {capitalize(product)}
                            {hasMoreThanOneOfASubscriptionItem ? ` (${quantity})` : ''}
                            {hasSkincareMinisInItems && ' (full-size)'}
                          </Text>
                        </Text>
                      </li>
                    ),
                  )}
                </List>
              </div>
            );
          })}

          <Box
            __maxWidth="315px"
            alignItems="center"
            backgroundColor="highlight.200"
            display="flex"
            justifyContent="center"
            paddingX="8x"
            paddingY="1x"
            width="full"
          >
            <Text __height="26px" __lineHeight="26px" __marginBottom="-4px" variant="bodyMd">
              As a subscriber, you save{' '}
              <b>{formatPriceWithCurrency({ currency, price: savingPrice })}</b>!
            </Text>
          </Box>
        </div>
        <Text color="primary.400" variant="bodyMd">
          FREE shipping. Manage or cancel products in your subscription anytime.
        </Text>
      </Box>
    </CheckoutBlock>
  );
};
